import 'intl-tel-input';

const INT_TEL_UTILS_URL =
  'https://cdnjs.cloudflare.com/ajax/libs/intl-tel-input/17.0.19/js/utils.min.js';

/**
 * Available formats:
 * - E164 (default) - +380441112233
 * - INTERNATIONAL - +380 44 111 2233
 * - NATIONAL - 044 111 2233
 * - RFC3966 - tel:+380-44-111-2233
 */
type PHONE_FORMAT = 'E164' | 'INTERNATIONAL' | 'NATIONAL' | 'RFC3966';

export class IntlTel {
  utilsPromise: Promise<typeof window.intlTelInputUtils> | null = null;

  /**
   * Checks whether provided phone is mobile or returns null otherwise
   */
  async parseMobileNumber(
    maybePhone: unknown,
    params: {
      /**
       * optional country that will be used if the phone is in local format
       * (without international code, e.g. +38)
       */
      country?: string;
      format?: PHONE_FORMAT;
    } = {},
  ): Promise<string | null> {
    if (typeof maybePhone !== 'string') {
      return null;
    }

    const phone = maybePhone;

    const formattedPhone = await this.parseNumber(phone, params);

    if (
      !formattedPhone ||
      !(await this.isMobileNumber(formattedPhone, params))
    ) {
      return null;
    }

    return formattedPhone;
  }

  async isMobileNumber(
    phone: string,
    {
      country,
    }: {
      /**
       * optional country that will be used if the phone is in local format
       * (without international code, e.g. +38)
       */
      country?: string;
    } = {},
  ): Promise<boolean> {
    const utils = await this.getUtils();
    // we need to call formatNumber to normalize phone and ensure it has leading '+' sign
    const formattedPhone = await this.parseNumber(phone, { country });

    if (!formattedPhone) {
      return false;
    }

    const numberType = utils.getNumberType(formattedPhone, country || '');

    return [
      utils.numberType.MOBILE,
      // Note that in the US there's no way to differentiate between
      // fixed-line and mobile numbers, so instead it will
      // return FIXED_LINE_OR_MOBILE.
      utils.numberType.FIXED_LINE_OR_MOBILE,
    ].includes(numberType);
  }

  /**
   * Returns formatted phone or null if it is invalid
   */
  async parseNumber(
    maybePhone: unknown,
    {
      format,
      country,
    }: {
      /**
       * optional country that will be used if the phone is in local format
       * (without international code, e.g. +38)
       */
      country?: string;

      /**
       * Available formats:
       * - E164 (default) - +380441112233
       * - INTERNATIONAL - +380 44 111 2233
       * - NATIONAL - 044 111 2233
       * - RFC3966 - tel:+380-44-111-2233
       */
      format?: PHONE_FORMAT;
    } = {},
  ): Promise<string | null> {
    if (typeof maybePhone !== 'string') {
      return null;
    }

    let phone = maybePhone;

    const utils = await this.getUtils();

    // handle international format e.g. +380441112233
    // or local format e.g. 0441112233
    let isValid = await this.isValid(phone, { country });

    if (!isValid && phone[0] !== '+') {
      // handle the phone in international format without leading '+'
      // e.g. 380441112233
      phone = `+${phone}`;
      isValid = await this.isValid(phone, { country });
    }

    if (!isValid) {
      return null;
    }

    return utils.formatNumber(
      phone,
      country || '',
      intlTelInputUtils.numberFormat[String(format)] ||
        intlTelInputUtils.numberFormat.E164,
    );
  }

  async isValid(
    maybePhone: unknown,
    {
      country,
    }: {
      /**
       * optional country that will be used if the phone is in local format
       * (without international code, e.g. +38)
       */
      country?: string;
    } = {},
  ): Promise<boolean> {
    if (typeof maybePhone !== 'string') {
      return false;
    }

    const phone = maybePhone;
    const utils = await this.getUtils();

    return !!utils.isValidNumber(phone, country || '');
  }

  private async getUtils(): Promise<typeof window.intlTelInputUtils> {
    if (!this.utilsPromise) {
      this.utilsPromise = Promise.resolve().then(async () => {
        if (window.intlTelInputUtils) {
          return window.intlTelInputUtils;
        }

        let maybePromise: Promise<void> | null = (
          window.intlTelInputGlobals as any
        ).loadUtils(INT_TEL_UTILS_URL);

        if (!maybePromise || !('then' in maybePromise)) {
          // @ts-ignore - we are accessing private prop, thats why it's not available
          maybePromise = Object.values(window.intlTelInputGlobals.instances)[0]
            .promise;
        }

        await maybePromise;

        return window.intlTelInputUtils;
      });
    }

    return this.utilsPromise;
  }
}

export const intlTel = new IntlTel();
